import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';

export type Zoom = 'fit' | number;

const PAPER_WIDTH = 880; // 8.5in page (816px) + gutters

/**
 * The desk: a deep-blue work surface the real document pages float on.
 * Zoom is a CSS transform; hit-testing and rects stay correct because
 * everything is visual-coordinate based.
 */
export function Desk({ deskRef, zoom, children }: {
  deskRef: RefObject<HTMLDivElement | null>;
  zoom: Zoom;
  children: ReactNode;
}) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [innerHeight, setInnerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const desk = deskRef.current;
    const inner = innerRef.current;
    if (!desk || !inner) return;
    const measure = () => {
      setInnerHeight(inner.offsetHeight);
      setContainerWidth(desk.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(desk);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [deskRef]);

  const scale =
    zoom === 'fit' ? Math.min(1, Math.max(0.35, (containerWidth - 56) / PAPER_WIDTH)) : zoom;

  return (
    <div ref={deskRef} className="relative min-h-0 flex-1 overflow-auto bg-desk py-8">
      <div style={{ width: PAPER_WIDTH * scale, height: innerHeight * scale, margin: '0 auto' }}>
        <div
          ref={innerRef}
          style={{ width: PAPER_WIDTH, transform: `scale(${scale})`, transformOrigin: 'top left' }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
